// Timeline items: be.web events and our own records (app payments, audit log, close-out) in one shape
// {k, ts, label, cat, sev, gate, amount, user, detail, plate, source: 'beweb' | 'lurdy', noise, context}
import { CATEGORIES, SEVERITY, describe, gateLabel } from '../../helpers/parkingEvents';
import { PAYMENT_STATUS_LABELS } from '../../pages/payments/paymentFormat';

const { INFO, WARN, FAIL } = SEVERITY;
const MIN = 60e3;

// swimlanes of the chart, top to bottom
export const LANES = Object.freeze([
    { id: 'move', label: 'Mozgás', cats: ['move', 'pass'] },
    { id: 'pay', label: 'Fizetés', cats: ['pay', 'payst', 'discount'] },
    { id: 'fail', label: 'Hibák', cats: ['reject', 'error'] },
    { id: 'app', label: 'App / backoffice', cats: ['app'] },
    { id: 'site', label: 'Sorompó / operátor', cats: ['device', 'operator', 'capacity', 'other'] },
]);

export const laneOf = (cat) => LANES.find((l) => l.cats.includes(cat)) || LANES[LANES.length - 1];
export const catColor = (cat) => (CATEGORIES[cat] || CATEGORIES.other).color;

// backend sends local "YYYY-MM-DD HH:MM:SS"
const ms = (s) => (s ? new Date(String(s).replace(' ', 'T')).getTime() : NaN);

export function bewebItem(e, context = false) {
    const d = describe(e);
    return {
        k: e.k,
        id: e.id,
        ts: e.ts,
        label: d.label,
        cat: d.cat,
        sev: d.sev,
        noise: !!d.noise,
        gate: e.gate,
        gateLabel: gateLabel(e.gate),
        amount: e.amount || 0,
        user: e.user || '',
        plate: e.plate || '',
        type: e.type,
        detail: [e.kind, e.alias, e.dialog].filter(Boolean).join(' · '),
        dir: e.dir,
        source: 'beweb',
        context,
    };
}

const FAILED_PAYMENT = ['TIMEOUT', 'CANCELLED', 'NOTAUTHORIZED', 'FRAUD', 'REVERSED', 'REFUND'];

// entries of our audit log that matter for a parking; "Lurdy szerver" = our PHP API talking to the parking system
const SERVER = 'Lurdy szerver';

// payments of our DB (SimplePay started in the app, or the admin's free payment on close-out)
export function paymentItems(payments) {
    const out = [];
    payments.forEach((p) => {
        const base = {
            cat: 'app',
            gate: '',
            gateLabel: 'App',
            amount: p.amount,
            plate: '',
            source: 'lurdy',
            orderref: p.orderref,
        };
        if (p.byAdmin) {
            out.push({
                ...base,
                k: `pay-${p.orderref}`,
                ts: ms(p.created_at),
                label: 'Admin kiengedte (ingyenes fizetés)',
                sev: WARN,
                user: p.byAdmin.name || p.byAdmin.email || '',
                detail: p.orderref,
            });
            return;
        }
        out.push({
            ...base,
            k: `pay-${p.orderref}`,
            ts: ms(p.created_at),
            label: 'App fizetés indítva',
            sev: INFO,
            user: p.email,
            detail: `SimplePay ${p.orderref}`,
        });
        if (p.status !== 'STARTED' && p.updated_at && p.updated_at !== p.created_at) {
            out.push({
                ...base,
                k: `pay-${p.orderref}-end`,
                ts: ms(p.updated_at),
                label: `App fizetés: ${PAYMENT_STATUS_LABELS[p.status] || p.status}`,
                sev: FAILED_PAYMENT.includes(p.status) ? WARN : INFO,
                user: p.email,
                detail: `SimplePay ${p.orderref} · ${p.status}`,
                finished: p.status === 'FINISHED',
            });
        }
    });
    return out;
}

const AUDIT = {
    // payTicket: after a successful SimplePay payment (or an admin close-out) our server marks the ticket paid
    parking_ticket_pay: (d) => ({
        label: `${SERVER}: jegy kifizetve a parkoló rendszerben`,
        sev: INFO,
        detail: [
            'a sikeres app fizetés / kiengedés után (payTicket), így nyílik a sorompó',
            d.discounts?.length ? `${d.discounts.length} kedvezménnyel` : '',
        ]
            .filter(Boolean)
            .join(' · '),
    }),
    parking_ticket_error: (d) => ({
        label: `${SERVER}: a parkoló rendszer hibát adott`,
        sev: FAIL,
        detail: d.error || '',
    }),
    parking_payment_amount_mismatch: (d) => ({
        label: `${SERVER}: a díj nőtt az app fizetés alatt`,
        sev: WARN,
        detail: `fizetve ${d.paid} Ft, esedékes ${d.due} Ft`,
    }),
    parking_ticket_discount: () => ({
        label: 'Kedvezmény beváltva az appban',
        sev: INFO,
        cat: 'discount',
    }),
    parking_ticket_close: (d) => ({
        label: d.paid ? 'Kézi lezárás és kiengedés' : 'Kézi lezárás',
        sev: WARN,
        detail: d.comment || '',
    }),
};

// the ticket's own entries in the range (an older API ignores the filters and returns everything)
export function auditItems(logs, ticketId, from, to) {
    return logs
        .filter(
            (l) =>
                AUDIT[l.action] &&
                String(l.details?.ticketid) === String(ticketId) &&
                ms(l.created_at) >= from &&
                ms(l.created_at) <= to
        )
        .map((l) => ({
            k: `audit-${l.id}`,
            ts: ms(l.created_at),
            cat: 'app',
            gate: '',
            gateLabel: SERVER,
            amount: 0,
            user: l.user_id ? `#${l.user_id}` : '',
            plate: '',
            source: 'lurdy',
            action: l.action,
            ...AUDIT[l.action](l.details || {}),
        }));
}

const pad = (n) => String(n).padStart(2, '0');
export const clock = (t, seconds = false) => {
    const d = new Date(t);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}${seconds ? `:${pad(d.getSeconds())}` : ''}`;
};
export const dateClock = (t) => {
    const d = new Date(t);
    return `${pad(d.getMonth() + 1)}.${pad(d.getDate())}. ${clock(t, true)}`;
};
export function duration(msLen) {
    const m = Math.round(msLen / MIN);
    if (m < 60) return `${m} perc`;
    const h = Math.floor(m / 60);
    return h < 48 ? `${h} óra ${m % 60} perc` : `${Math.floor(h / 24)} nap ${h % 24} óra`;
}

/**
 * What happened, in a few sentences, plus the things that look wrong (for the alert box above the timeline)
 */
export function diagnose(items) {
    const own = items.filter((i) => !i.context);
    const moves = own.filter((i) => i.type === 'ID moved');
    const entry = moves.find((i) => i.dir === 'in');
    const exit = [...moves].reverse().find((i) => i.dir === 'out');
    const paid = own.filter((i) => i.type === 'ID paid');
    const rejects = own.filter((i) => i.type === 'ID rejected unpaid');
    const errors = own.filter((i) => i.cat === 'error' || i.cat === 'reject');
    const appFinished = own.filter((i) => i.finished);
    const findings = [];

    if (rejects.length) {
        const first = rejects[0];
        const after = exit && exit.ts > first.ts ? exit : null;
        const payAfter = paid.find((p) => p.ts > first.ts);
        findings.push({
            sev: WARN,
            text: `${rejects.length}× elutasítva a kijáratnál (nem volt fizetve, először ${
                first.gateLabel
            })${payAfter ? `, utána fizetett: ${payAfter.gateLabel}` : ''}${
                after ? `; ${Math.round((after.ts - first.ts) / MIN)} perc múlva hajtott ki.` : '.'
            }`,
            focus: first.k,
        });
        // let out by hand: barrier opened / mode changed at that gate around the exit, without a payment
        if (after && !payAfter) {
            const opened = items.find(
                (i) =>
                    i.context &&
                    i.gate === after.gate &&
                    Math.abs(i.ts - after.ts) < 5 * MIN &&
                    /Open|Free exit|unknown source/.test(i.type || '')
            );
            findings.push({
                sev: FAIL,
                text: opened
                    ? `Fizetés nélkül hajtott ki: ${opened.label} (${opened.gateLabel}${
                          opened.user ? `, ${opened.user}` : ''
                      }).`
                    : 'Fizetés nélkül hajtott ki az elutasítás után (nincs fizetés az eseményekben).',
                focus: (opened || after).k,
            });
        }
    }
    // app payment finished, but the parking system never got it (only if we have the parking system's events at all)
    const haveBeweb = own.some((i) => i.source === 'beweb');
    if (!haveBeweb) {
        findings.push({
            sev: 'info',
            text: 'Ehhez a parkoláshoz nincs esemény a parkoló rendszerből (az archívumban még nincs meg ez a nap, vagy nem fut a szinkron), csak az app és a Lurdy szerver eseményei látszanak.',
        });
    }
    (haveBeweb ? appFinished : []).forEach((a) => {
        const soap = paid.find((p) => p.gate === 'SOAPPay' && Math.abs(p.ts - a.ts) < 15 * MIN);
        if (!soap) {
            findings.push({
                sev: FAIL,
                text: `Az appban sikeresen fizetett (${a.amount} Ft), de a parkoló rendszerben nem látszik a fizetés (SOAPPay „ID paid” hiányzik).`,
                focus: a.k,
            });
        }
    });
    const byType = {};
    errors
        .filter((i) => i.cat === 'error')
        .forEach((i) => {
            byType[i.label] = (byType[i.label] || []).concat(i);
        });
    Object.entries(byType).forEach(([label, list]) => {
        const gates = [...new Set(list.map((i) => i.gateLabel))].join(', ');
        findings.push({
            sev: list[0].sev,
            text: `${list.length}× ${label} (${gates})`,
            focus: list[0].k,
        });
    });
    if (own.some((i) => i.sev === FAIL && i.source === 'lurdy')) {
        const f = own.find((i) => i.sev === FAIL && i.source === 'lurdy');
        findings.push({ sev: FAIL, text: `${f.label}: ${f.detail}`, focus: f.k });
    }

    const paidSum = paid.reduce((s, p) => s + (p.amount || 0), 0);
    const story = [
        entry && `Behajtás ${clock(entry.ts)} (${entry.gateLabel})`,
        paid.length > 0 &&
            `fizetve ${new Intl.NumberFormat('hu-HU').format(paidSum)} Ft (${[
                ...new Set(paid.map((p) => p.gateLabel)),
            ].join(', ')})`,
        exit && `kihajtás ${clock(exit.ts)} (${exit.gateLabel})`,
        entry && exit && `${duration(exit.ts - entry.ts)} bent`,
    ].filter(Boolean);
    return { story, findings, entry, exit };
}

/**
 * Filter state shared by the timelines and the live view
 * @param items
 * @param f {{cats: Set|null, problemsOnly: boolean, noise: boolean, context: boolean, types: string[], gates: string[]}}
 */
export function applyFilter(items, f) {
    return items.filter(
        (i) =>
            (f.noise || !i.noise) &&
            (f.context !== false || !i.context) &&
            (!f.problemsOnly || i.sev !== INFO) &&
            (!f.cats || f.cats.has(i.cat)) &&
            (!f.types?.length || f.types.includes(i.label)) &&
            (!f.gates?.length || f.gates.includes(i.gate))
    );
}
